import { useCallback, useEffect, useRef, useState } from 'react';
import EnlacesLegales from '../ui/EnlacesLegales';
import { ArrowLeft, Loader2, Fingerprint, Info } from 'lucide-react';
import { useAuth } from '../lib/contextoAuth';
import { claveEmpresaRecordada } from '../lib/claveEmpresa';
import {
  MarcoPuerta, PanelVisual, TexturaPuntos, CampoPuerta, BotonPuerta,
  AvisoError, Plegado,
} from '../publico/puerta';
import { CLASE_CAMPO } from '../publico/medidas';
import { api } from '../api';
import CampoContrasena from '../ui/CampoContrasena';
import Ayuda from '../ui/Ayuda';
import {
  navegadorAdmiteLlaves, rellenoAutomaticoDisponible, aparatoSinLlave, llaveNoRegistrada, ENLACE_LLAVES,
  RELEVO_CONDICIONAL_MS, textoDeFalloDeLlave,
} from '../seguridad/llaveDeAcceso';
import { reescribirUrlEn } from '../ui/navegacion';
import { limpiarCodigo, codigoCompleto } from '../seguridad/segundoFactor';

// Peaje U-02: la ayuda de capa 2 con la tinta de apoyo de la puerta (sigue al tema público).
const AYUDA_PUERTA = '[&>span]:text-[var(--p-tinta-2)]';
// «Ver» de la contraseña con la tinta de la puerta, como en RegistroEmpresa (en oscuro daba 2,28:1).
const BOTON_VER_PUERTA = 'text-[var(--p-tinta-2)] hover:text-[var(--p-tinta)]';

function RecuperarPorMiCuenta({ empresa, usuario }) {
  const [clave, setClave] = useState(empresa || '');
  const [quien, setQuien] = useState(usuario || '');
  const [respuesta, setRespuesta] = useState(null);
  const [enviando, setEnviando] = useState(false);
  async function pedir() {
    setEnviando(true);
    try {
      const r = await api.post('/auth/recuperar', { clave_acceso: clave.trim(), usuario: quien.trim() });
      setRespuesta(r.mensaje);
    } catch (e) {
      setRespuesta(e.message || 'No se pudo pedir. Revisa la conexión e inténtalo otra vez.');
    } finally {
      setEnviando(false);
    }
  }
  if (respuesta) return <p role="status" className="mt-2 text-sm text-gray-700">{respuesta}</p>;
  return (
    <div className="mt-2 space-y-2">
      <input value={clave} onChange={(e) => setClave(e.target.value)} placeholder="Clave de tu empresa" aria-label="Clave de tu empresa" className={CLASE_CAMPO} />
      <input value={quien} onChange={(e) => setQuien(e.target.value)} placeholder="Tu usuario o tu correo" aria-label="Tu usuario o tu correo" className={CLASE_CAMPO} />
      <button type="button" onClick={pedir} disabled={enviando || !clave.trim() || !quien.trim()}
              className="text-sm font-semibold text-[var(--color-marca)] disabled:opacity-50">
        {enviando ? 'Pidiendo…' : 'Pedir una contraseña temporal'}
      </button>
    </div>
  );
}

/* =====================================================================
   LA PUERTA DE LOS CLIENTES · entrar
   =====================================================================
   La cara comercial: para mucha gente es la primera pantalla de TEGO que
   ven. Comparte esqueleto y medidas con el alta de empresa y con la
   consola de operador —viven en `publico/puerta.jsx`— y pone encima su
   carácter: azul de marca con el rescoldo ámbar abajo a la izquierda, y
   las formas redondas.

   NADA TRAÍDO DE FUERA: el degradado y la textura se dibujan aquí.
   ===================================================================== */
// POR QUÉ VOLVISTE A ESTA PANTALLA · deudas T-a y T-b.
//
// EN TONO TRANQUILO, NO EN ROJO, y eso es medio arreglo. Que una sesión se
// cierre sola no es un error de quien estaba trabajando: es lo que el
// sistema hace a propósito. En rojo competiría con el error de
// credenciales —que sí es un problema que atender ahora— y daría a
// entender que algo se rompió.
//
// Y DICE QUE NO SE PERDIÓ NADA, que es lo primero que quiere saber quien
// llevaba media hora escribiendo.
//
// (Reinjertado el 19 de agosto de 2026: el rediseño de las dos puertas se
// lo llevó por delante al reescribir este archivo. La prueba
// `test_auditoria_EL_AVISO_DE_SESION_NO_SALE_EN_ROJO` lo cazó.)
const PORQUE_SALISTE = {
  CADUCADA: {
    titulo: 'Tu sesión se cerró por seguridad',
    detalle: 'Había pasado el tiempo máximo. Vuelve a entrar y regresas a '
           + 'la misma pantalla, con lo que estabas escribiendo.',
  },
  INACTIVIDAD: {
    titulo: 'Cerramos tu sesión porque llevabas un rato sin usarla',
    detalle: 'Así un equipo olvidado no se queda abierto. Vuelve a entrar '
           + 'y regresas a la misma pantalla, con lo que estabas '
           + 'escribiendo.',
  },
};

export default function Login({ onCrearCuenta }) {
  const { iniciarSesion, iniciarSesionConUsuario, iniciarSesionConLlave, motivoCierre } = useAuth();
  const porqueSaliste = PORQUE_SALISTE[motivoCierre] || null;
  // ================================================================
  // DOS CAMINOS, Y EL SEGUNDO NO ES UN «MODO AVANZADO» · Fase 1
  // ================================================================
  // En una bodega grande, la mayoría de la plantilla NO tiene correo de
  // empresa: entra con el usuario que le puso su encargado. Así que el
  // segundo camino no se esconde detrás de nada, y si este navegador ya
  // recuerda la clave de una empresa, se abre directamente en él —que
  // es donde entró la última vez desde ese aparato.
  const recordada = claveEmpresaRecordada();
  const [via, setVia] = useState(recordada ? 'usuario' : 'correo');
  // Peaje U-02: «Usar un código de respaldo» solo cambia la ayuda del campo.
  const [conRespaldo, setConRespaldo] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [empresa, setEmpresa] = useState(recordada);
  const [nombreUsuario, setNombreUsuario] = useState('');
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(false);
  // NO SE REGAÑA POR ADELANTADO. Nada se escribe en rojo hasta que
  // alguien ha intentado entrar: antes de eso no ha hecho nada mal.
  const [intento, setIntento] = useState(false);
  // EL PASO DEL SEGUNDO FACTOR · ESTABILIDAD D1/D4. Aparece solo cuando el
  // servidor dice que la contraseña fue buena y falta el código: así no se
  // le enseña a nadie un campo que no le toca.
  const [pideCodigo, setPideCodigo] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [recordar, setRecordar] = useState(false);
  // LA LLAVE DE ACCESO · DIRECTRIZ-LLAVE L-2. Cuatro estados a la vista:
  // `listo`, `esperando` (la ventana del aparato está abierta), `sin-llave`
  // (el aparato no dio ninguna: gris, no es un problema) y `error` (rojo).
  // El quinto, «este navegador no las admite», no es un estado: es el
  // navegador, y se pregunta en cada pintado.
  const admiteLlaves = navegadorAdmiteLlaves();
  const [llave, setLlave] = useState({ estado: 'listo', error: null });
  const [rumboALlaves, setRumboALlaves] = useState(false);
  // UNA SOLA CEREMONIA VIVA. La del relleno automático se guarda aquí para
  // poder cortarla antes de abrir la del botón: dos `get()` a la vez y el
  // navegador rechaza la segunda.
  const condicional = useRef(null);
  const montada = useRef(true);
  // La función de la sesión cambia de identidad en cada pintado del
  // proveedor; el relleno automático no tiene por qué cortarse por eso.
  const conLlaveRef = useRef(iniciarSesionConLlave);
  useEffect(() => { conLlaveRef.current = iniciarSesionConLlave; }, [iniciarSesionConLlave]);

  const porCorreo = via === 'correo';
  const falta = [];
  if (porCorreo) {
    if (!email.trim()) falta.push({ campo: 'correo', dice: 'tu correo' });
  } else {
    if (!empresa.trim()) falta.push({ campo: 'empresa', dice: 'la clave de tu empresa' });
    if (!nombreUsuario.trim()) falta.push({ campo: 'usuario', dice: 'tu usuario' });
  }
  if (!password) falta.push({ campo: 'clave', dice: 'tu contraseña' });
  if (pideCodigo && !codigoCompleto(codigo)) {
    falta.push({ campo: 'codigo-acceso', dice: 'el código de tu aplicación' });
  }
  const dice = `Falta ${falta.map((f) => f.dice).join(' y ')}.`;

  function cambiarVia(nueva) {
    setVia(nueva);
    setError(null);
    setIntento(false);
    setPideCodigo(false);
    setCodigo('');
  }

  async function enviar(e) {
    e.preventDefault();
    if (cargando) return;
    if (falta.length) {
      // El botón se puede pulsar aunque no esté todo: así puede DECIR qué
      // falta y llevar el cursor allí, en vez de quedarse mudo y gris.
      setIntento(true);
      document.getElementById(falta[0].campo)?.focus();
      return;
    }
    setError(null);
    setCargando(true);
    try {
      // El paso del código vale para las dos puertas (corrección F4b).
      const extra = pideCodigo
        ? { codigo: limpiarCodigo(codigo), recordarAparato: recordar } : undefined;
      if (porCorreo) await iniciarSesion(email.trim(), password, extra);
      else await iniciarSesionConUsuario(
        empresa.trim().toUpperCase(), nombreUsuario.trim().toLowerCase(), password, extra);
      // El re-render lo dispara el cambio de estado de sesion en App.
    } catch (err) {
      if (err.codigo === 'SEGUNDO_FACTOR' && !pideCodigo) {
        // La contraseña fue buena: ahora toca el código. No es un error.
        setPideCodigo(true);
        setError(null);
        setCargando(false);
        setTimeout(() => document.getElementById('codigo-acceso')?.focus(), 0);
        return;
      }
      setError(err.message || 'No se pudo iniciar sesión.');
      setCargando(false);
    }
  }

  // EL RELLENO AUTOMÁTICO. Se lanza al montar si el navegador lo ofrece y
  // deja las llaves del aparato en el menú del campo de correo. No abre
  // ninguna ventana: solo termina si la persona elige una llave. Cortarla
  // (el botón, salir de la pantalla, el relevo del reto) no es un error y
  // no se dice nada; un rechazo DESPUÉS de elegir una llave, sí.
  const cortarCondicional = useCallback(() => {
    const viva = condicional.current;
    condicional.current = null;
    viva?.abort();
  }, []);

  const lanzarCondicional = useCallback(async () => {
    if (!montada.current || condicional.current) return;
    if (!(await rellenoAutomaticoDisponible())) return;
    if (!montada.current || condicional.current) return;
    const control = new AbortController();
    condicional.current = control;
    const relevo = setTimeout(() => {
      if (condicional.current !== control) return;
      cortarCondicional();
      lanzarCondicional();
    }, RELEVO_CONDICIONAL_MS);
    try {
      await conLlaveRef.current(undefined, { mediation: 'conditional', signal: control.signal });
    } catch (err) {
      if (control.signal.aborted || err?.fase !== 'entrar') return;
      if (!montada.current) return;
      if (llaveNoRegistrada(err)) setLlave({ estado: 'sin-llave', error: null, texto: err.message });
      else setLlave({ estado: 'error', error: textoDeFalloDeLlave(err) });
    } finally {
      clearTimeout(relevo);
      if (condicional.current === control) condicional.current = null;
    }
  }, [cortarCondicional]);

  useEffect(() => {
    montada.current = true;
    lanzarCondicional();
    return () => {
      montada.current = false;
      cortarCondicional();
    };
  }, [lanzarCondicional, cortarCondicional]);

  // EL BOTÓN. No pide el correo: si está escrito viaja como pista y nada
  // más. Corta antes la ceremonia del relleno automático.
  async function entrarConLlave() {
    if (!admiteLlaves || llave.estado === 'esperando') return;
    cortarCondicional();
    setError(null);
    setRumboALlaves(false);
    setLlave({ estado: 'esperando', error: null });
    try {
      await iniciarSesionConLlave(porCorreo && email.trim() ? email.trim() : undefined);
      // El re-render lo dispara el cambio de estado de sesión en App.
    } catch (err) {
      if (!montada.current) return;
      if (aparatoSinLlave(err)) setLlave({ estado: 'sin-llave', error: null });
      else if (llaveNoRegistrada(err)) setLlave({ estado: 'sin-llave', error: null, texto: err.message });
      else setLlave({ estado: 'error', error: textoDeFalloDeLlave(err) });
      lanzarCondicional();
    }
  }

  // «IR A LLAVES DE ACCESO» SIN SALIR DE AQUÍ. Para agregar una llave hay
  // que entrar primero, así que el enlace no recarga nada: deja la
  // dirección apuntando a Mi cuenta → Llaves de acceso (sobrevive al
  // inicio de sesión) y lo dice. El `href` de verdad se queda para quien
  // lo abra en otra pestaña o lo copie.
  function irALlaves(e) {
    e.preventDefault();
    try {
      reescribirUrlEn(window, `${window.location.pathname}${ENLACE_LLAVES.slice(ENLACE_LLAVES.indexOf('?'))}`);
    } catch { /* sin historial: el enlace normal lo hará */ }
    setRumboALlaves(true);
    const primero = porCorreo ? (email.trim() ? 'clave' : 'correo') : (nombreUsuario.trim() ? 'clave' : 'usuario');
    document.getElementById(primero)?.focus();
  }

  return (
    <MarcoPuerta panel={<PanelDeMarca />}>
      {/* «VOLVER AL SITIO» Y NO «AL INICIO», y es un enlace de verdad.
          El inicio de la consola ya no existe: desde el 30 de agosto de
          2026 la portada vive en `/`, fuera de la aplicación. Un
          `<a href="/">` deja además copiar el enlace y abrirlo en otra
          pestaña, que un `<button>` no. */}
      <a
        href="/"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--p-tinta-2)] hover:text-[var(--p-tinta)] mb-8 transition-colors duration-150"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Volver al sitio
      </a>

      {/* En teléfono el panel de la izquierda no existe: sin esto la
          pantalla no diría de quién es el formulario que pide una
          contraseña. Y el logo lleva al sitio, que es lo que espera
          quien lo pulsa. */}
      <a href="/" className="lg:hidden block text-xl font-bold tracking-tight mb-6">
        TEGO<span className="text-[var(--p-marca)]">Cloud</span>
      </a>

      <h1 className="text-[1.75rem] leading-tight font-semibold tracking-tight">
        Entra a tu cuenta
      </h1>
      <p className="mt-2 text-[0.9375rem] leading-relaxed text-[var(--p-tinta-2)]">
        {porCorreo
          ? 'Con el correo y la contraseña que te dio tu empresa.'
          : 'Con el usuario y la contraseña que te dio tu encargado.'}
      </p>

      {/* LAS DOS PUERTAS, A LA VISTA Y CON TEXTO, NUNCA SOLO UN ICONO.
          Quien no tiene correo tiene que ver desde el primer segundo que
          esta pantalla también es para él: si el segundo camino viviera
          detrás de un enlace pequeño abajo, la mitad de la plantilla de
          una bodega se quedaría mirando un campo de correo que no tiene. */}
      <div role="tablist" aria-label="Cómo vas a entrar"
           className="mt-6 grid grid-cols-2 gap-2">
        {[
          { id: 'correo', titulo: 'Con mi correo', pie: 'Si tienes correo de la empresa' },
          { id: 'usuario', titulo: 'Con mi usuario', pie: 'Si te dieron un usuario' },
        ].map((op) => (
          <button
            key={op.id} type="button" role="tab"
            aria-selected={via === op.id}
            onClick={() => cambiarVia(op.id)}
            className={`rounded-lg border px-3 py-2.5 text-left transition-colors duration-150 ${
              via === op.id
                ? 'border-[var(--p-marca)] bg-[var(--p-fondo-2)]'
                : 'border-[var(--p-linea)] hover:border-[var(--p-tinta-3)]'}`}
          >
            <span className="block text-sm font-semibold">{op.titulo}</span>
            <span className="block text-[0.75rem] leading-snug text-[var(--p-tinta-3)]">
              {op.pie}
            </span>
          </button>
        ))}
      </div>

      {/* ARRIBA DEL FORMULARIO: se lee antes de empezar a escribir, no
          después de rellenarlo todo. Y `status`, no `alert`: no es una
          urgencia, es una explicación. */}
      {porqueSaliste && (
        <div
          role="status"
          className="mt-6 rounded-lg border border-[var(--p-linea)]
                     border-l-[3px] border-l-[var(--p-marca)]
                     bg-[var(--p-fondo-2)] px-4 py-3"
        >
          <p className="text-sm font-semibold">{porqueSaliste.titulo}</p>
          <p className="mt-1 text-sm leading-relaxed text-[var(--p-tinta-2)]">
            {porqueSaliste.detalle}
          </p>
        </div>
      )}

      <form onSubmit={enviar} className="mt-8 space-y-5" noValidate>
        {porCorreo ? (
          <CampoPuerta id="correo" etiqueta="Correo electrónico"
                       ayuda="El mismo con el que te dieron de alta.">
            <input
              id="correo" type="email" inputMode="email" autoComplete="username webauthn" autoFocus
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="nombre@tuempresa.com" className={CLASE_CAMPO}
            />
          </CampoPuerta>
        ) : (
          <>
            <CampoPuerta
              id="empresa" etiqueta="Clave de tu empresa"
              ayuda="Viene impresa en la hoja que te dieron. Este aparato la recuerda para la próxima vez."
            >
              <input
                id="empresa" type="text" autoComplete="organization"
                autoFocus={!recordada}
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value.toUpperCase())}
                placeholder="TORNILLOS-MX" className={CLASE_CAMPO}
              />
            </CampoPuerta>
            <CampoPuerta id="usuario" etiqueta="Tu usuario"
                         ayuda="Te lo puso tu encargado. Va en minúsculas.">
              <input
                id="usuario" type="text" autoComplete="username webauthn"
                autoFocus={!!recordada}
                value={nombreUsuario}
                onChange={(e) => setNombreUsuario(e.target.value.toLowerCase())}
                placeholder="jlopez" className={CLASE_CAMPO}
              />
            </CampoPuerta>
          </>
        )}

        <CampoPuerta id="clave" etiqueta="Contraseña">
          <CampoContrasena
            id="clave" autoComplete="current-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className={CLASE_CAMPO}
            claseBoton={BOTON_VER_PUERTA}
          />
          <Plegado resumen="¿Olvidaste tu contraseña?">
            {/* CUENTA A5 (13 sep 2026): «con lo que exista, en este orden».
                Primero el medio que nunca falla —quien administra—, y
                después el autoservicio, que solo manda algo si la persona
                tiene un medio verificado y este servidor manda correo. La
                respuesta es la misma en todos los casos, a propósito. */}
            <p>
              Te la repone quien administra TEGO en tu empresa: en{' '}
              <span className="font-semibold">Personal</span> te busca y elige{' '}
              <span className="font-semibold">Restablecer contraseña</span>. Siempre puede.
            </p>
            <p className="mt-2">
              Si en <span className="font-semibold">Mi cuenta</span> dejaste un correo personal de
              recuperación verificado, pide aquí una contraseña temporal:
            </p>
            <RecuperarPorMiCuenta empresa={empresa} usuario={porCorreo ? email : nombreUsuario} />
            {/* ESTABILIDAD F6: si se perdió TODO (contraseña, teléfono, códigos). */}
            <p className="mt-3">
              ¿Perdiste todo el acceso a la cuenta del Admin Supremo?{' '}
              <a href="/app?ir=recuperar" className="font-semibold underline">Recuperar mi cuenta</a>
            </p>
          </Plegado>
        </CampoPuerta>

        {pideCodigo && (
          <CampoPuerta id="codigo-acceso" etiqueta="Código de tu aplicación">
            {/* Peaje U-02: mayúsculas y sin corrector (admite códigos de respaldo). SIN maxLength:
                el navegador recortaría lo pegado ANTES de limpiarlo («  DFV72-7H7QF» → «DFV72-7H7Q»);
                el tope lo pone limpiarCodigo después de quitar los espacios. */}
            <input
              id="codigo-acceso" inputMode="text" autoComplete="one-time-code"
              autoCapitalize="characters" spellCheck={false}
              aria-describedby="ayuda-codigo-acceso"
              value={codigo} onChange={(e) => setCodigo(limpiarCodigo(e.target.value))}
              placeholder="123456" className={CLASE_CAMPO}
            />
            {/* Peaje U-02: la ayuda de capa 2 pinta text-gray-600, que la puerta pública no remapea:
                en oscuro daba 2,58:1. Aquí toma la tinta de apoyo de la puerta (8,1:1). */}
            <div id="ayuda-codigo-acceso" className={AYUDA_PUERTA}>
              {conRespaldo ? <Ayuda id="seguridad.acceso.codigo_respaldo" /> : <Ayuda id="seguridad.acceso.codigo" />}
            </div>
            <button type="button" onClick={() => setConRespaldo((v) => !v)} aria-expanded={conRespaldo}
                    className="mt-1 min-h-11 text-left text-base font-semibold underline">
              {conRespaldo ? 'Usar el código de mi aplicación' : 'Usar un código de respaldo'}
            </button>
            <label className="mt-3 flex min-h-11 items-center gap-2 text-sm">
              <input type="checkbox" checked={recordar} onChange={(e) => setRecordar(e.target.checked)} className="h-5 w-5" />
              No volver a pedírmelo en este aparato por 30 días
            </label>
            <div className={AYUDA_PUERTA}><Ayuda id="seguridad.acceso.recordar_aparato" /></div>
          </CampoPuerta>
        )}

        <AvisoError>{error}</AvisoError>

        <div>
          <BotonPuerta
            apagado={cargando || (intento && falta.length > 0)}
            title={falta.length ? `${dice} En cuanto lo completes, el botón se enciende.` : undefined}
          >
            {cargando && <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden="true" />}
            {cargando ? 'Comprobando…' : 'Entrar'}
          </BotonPuerta>
          {intento && falta.length > 0 && (
            <p className="mt-2 text-[0.8125rem] text-[var(--p-brasa)]" role="status">{dice}</p>
          )}
        </div>
      </form>

      {/* LLAVE DE ACCESO · DIRECTRIZ-LLAVE L-2 (era ESTABILIDAD D3).
          SIEMPRE A LA VISTA, en las dos puertas y sin pedir el correo. Si
          el navegador no las admite, apagada con su motivo al lado: un
          botón que desaparece no le dice a nadie que existe. */}
      <div className="mt-4">
        <button
          type="button" id="entrar-con-llave" onClick={entrarConLlave}
          disabled={!admiteLlaves || llave.estado === 'esperando'}
          aria-describedby={admiteLlaves ? undefined : 'llave-sin-soporte'}
          aria-busy={llave.estado === 'esperando' || undefined}
          className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-[var(--p-linea)] px-4 py-2.5 text-sm font-semibold hover:border-[var(--p-tinta-3)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {llave.estado === 'esperando'
            ? <Loader2 className="h-4 w-4 shrink-0 motion-safe:animate-spin" aria-hidden="true" />
            : <Fingerprint className="h-4 w-4 shrink-0" aria-hidden="true" />}
          <span>
            {llave.estado === 'esperando'
              ? 'Esperando tu huella, Face ID o PIN…'
              : 'Entrar con llave de acceso'}
          </span>
        </button>
        {admiteLlaves ? (
          <div className={AYUDA_PUERTA}><Ayuda id="seguridad.acceso.llave" /></div>
        ) : (
          <p id="llave-sin-soporte" className="mt-2 text-[0.8125rem] leading-relaxed text-[var(--p-tinta-2)]">
            Este navegador no admite llaves de acceso.
          </p>
        )}

        {/* SIN LLAVE EN ESTE APARATO: GRIS, NO ROJO. Nadie hizo nada mal;
            es que aquí no hay llave. Dice qué hacer ahora y deja el camino
            a donde se agrega. */}
        {llave.estado === 'sin-llave' && (
          <div
            role="status" data-aviso="sin-llave" data-motivo={llave.texto ? 'no-registrada' : 'sin-llave'}
            className="mt-3 flex gap-2 rounded-lg border border-[var(--p-linea)] bg-[var(--p-fondo-2)] px-4 py-3 text-sm leading-relaxed text-[var(--p-tinta-2)]"
          >
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              {/* R-LL-11: si el aparato dio una llave que TEGO ya no tiene,
                  el texto es el del servidor (dice que se quitó). */}
              {llave.texto ? <p>{llave.texto}</p> : (
                <p>
                  En este aparato no hay una llave de acceso para TEGO. Entra con tu contraseña y
                  el código de tu autenticador; después, en Mi cuenta → Seguridad → Llaves de
                  acceso, agrega una con tu huella o Face ID y la próxima vez entras con un toque.
                </p>
              )}
              {rumboALlaves ? (
                <p className="mt-2 font-semibold text-[var(--p-tinta)]">
                  Listo: al entrar irás directo a Mi cuenta → Llaves de acceso.
                </p>
              ) : (
                <a
                  href={ENLACE_LLAVES} onClick={irALlaves} data-enlace="llaves"
                  className="mt-1 inline-flex min-h-11 items-center font-semibold text-[var(--p-marca)] underline"
                >
                  Ir a Llaves de acceso
                </a>
              )}
            </div>
          </div>
        )}

        {llave.estado === 'error' && (
          <div className="mt-3"><AvisoError>{llave.error}</AvisoError></div>
        )}
      </div>

      {/* EL PUENTE AL ALTA, QUE LA PORTADA HACÍA HASTA HOY.
          Mientras la consola pintaba su propia portada, quien llegaba
          aquí sin cuenta podía volver atrás y pulsar «Comienza gratis».
          Al apagarla, ese camino se quedaba en «Volver al sitio» y
          buscar el botón otra vez. Un enlace aquí lo ahorra. */}
      {onCrearCuenta && (
        <p className="mt-6 text-[0.9375rem] text-[var(--p-tinta-2)]">
          ¿No tienes cuenta?{' '}
          <button
            type="button"
            onClick={onCrearCuenta}
            className="font-semibold text-[var(--p-marca)] hover:underline"
          >
            Crea una gratis
          </button>
        </p>
      )}

      <p className="mt-10 text-[0.8125rem] leading-relaxed text-[var(--p-tinta-3)]">
        Tu sesión es de tu empresa: solo ves lo de la tuya.
      </p>
    </MarcoPuerta>
  );
}

function PanelDeMarca() {
  return (
    <PanelVisual
      fondo="linear-gradient(152deg, #0e5397 0%, #08355f 32%, #061e36 60%, #040c15 100%)"
      /* EL RESCOLDO. Un ámbar muy bajo abajo a la izquierda: es lo que
         separa «cálido» de «corporativo frío» sin meter otro color a la
         paleta, y lo que hace que esta puerta y la portada se reconozcan
         de la misma casa. */
      halo={
        'radial-gradient(46rem 32rem at 4% 96%, rgba(219,146,60,0.30), transparent 64%),' +
        'radial-gradient(40rem 30rem at 98% -8%, rgba(77,163,240,0.22), transparent 66%)'
      }
      textura={<TexturaPuntos id="entrar" />}
      marca={
        <span className="text-2xl font-bold tracking-tight">
          TEGO<span className="text-[#7cbdf7]">Cloud</span>
        </span>
      }
      frase="Saber qué hay, dónde está y cuánto queda."
      pie={
        <div className="flex flex-col gap-1">
          <span className="text-[0.8125rem] font-medium tracking-wide text-[var(--p-sobre-noche-2)]">
            Inventario para bodegas y tiendas · México
          </span>
          {/* CRITERIO 5.4 · aquí, donde se decide si se acepta. */}
          <EnlacesLegales />
        </div>
      }
    />
  );
}
